import axios from 'axios';

export type ChatLang = 'ru' | 'en' | 'zh';

export interface ChatThread {
  product_id: number;
  article: string | null;
  title: string | null;
  name?: string | null;
  eng_name?: string | null;
  desc?: string | null;
  eng_desc?: string | null;
  last_body_en?: string | null;
  last_body_ru?: string | null;
  last_body_zh?: string | null;
  last_body_original?: string | null;
  last_message_at?: string | null;
  has_unread?: boolean;
  unread_count?: number;
  thumb_url?: string | null;
}

export interface ChatMessage {
  id: number;
  product_id: number;
  sender_id: string;
  sender_username?: string | null;
  sender_role?: string | null;
  body_original?: string | null;
  source_lang: ChatLang;
  body_ru?: string | null;
  body_en?: string | null;
  body_zh?: string | null;
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_mime?: string | null;
  attachment_size?: number | null;
  created_at: string;
}

const client = axios.create({
  baseURL: '/api',
  timeout: 50000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface VedSourcerChatThread {
  order_id: number;
  internal_article?: string;
  last_body?: string;
  unread_count?: number;
  mention_unread_count?: number;
}

export interface VedSourcerChatMessage {
  id: number;
  order_id: number;
  sender_user_id: string;
  sender_label?: string | null;
  body?: string | null;
  mentioned_user_ids?: string[];
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_mime?: string | null;
  created_at: string;
}

export const vedSourcerChatAPI = {
  getThreads: () => client.get('/ved-sourcer-chat/threads') as Promise<{ threads: VedSourcerChatThread[] }>,
  mentionCandidates: (q: string) =>
    client.get('/ved-sourcer-chat/mention-candidates', { params: { q: q || '' } }) as Promise<{
      users: Array<{ id: string; label: string; username?: string | null; role?: string | null }>;
    }>,
  getMessages: (orderId: number) =>
    client.get(`/ved-sourcer-chat/${orderId}/messages`) as Promise<{
      internal_article?: string;
      messages: VedSourcerChatMessage[];
    }>,
  postMessage: (orderId: number, { body = '', mentioned_user_ids = [], file = null }: {
    body?: string;
    mentioned_user_ids?: string[];
    file?: File | null;
  }) => {
    const fd = new FormData();
    fd.append('body', body || '');
    fd.append('mentioned_user_ids', JSON.stringify(mentioned_user_ids || []));
    if (file) fd.append('file', file);
    return client.post(`/ved-sourcer-chat/${orderId}/messages`, fd) as Promise<VedSourcerChatMessage>;
  },
  markRead: (orderId: number) => client.post(`/ved-sourcer-chat/${orderId}/read`) as Promise<{ ok: boolean }>,
};

export const chatAPI = {
  getUnreadCount: () => client.get('/chat/unread-count') as Promise<{ success: boolean; count: number }>,
  getThreads: (params?: { q?: string; limit?: number; cursor?: string | null }) =>
    client.get('/chat/threads', { params: { ...params, cursor: params?.cursor || undefined } }) as Promise<{
      success: boolean;
      threads: ChatThread[];
      next_cursor: string | null;
    }>,
  getThread: (productId: string | number) =>
    client.get(`/chat/threads/${productId}`) as Promise<{ success: boolean; product: any; can_edit_tz: boolean }>,
  getMessages: (productId: string | number, params?: { limit?: number; before?: string }) =>
    client.get(`/chat/threads/${productId}/messages`, { params }) as Promise<{
      success: boolean;
      messages: ChatMessage[];
    }>,
  markRead: (productId: string | number) =>
    client.post(`/chat/threads/${productId}/read`) as Promise<{ success: boolean; product_id: number }>,
  sendMessage: (productId: string | number, data: FormData) =>
    client.post(`/chat/threads/${productId}/messages`, data) as Promise<{
      success: boolean;
      message: ChatMessage;
    }>,
  updateTz: (productId: string | number, fields: Record<string, string | null>) =>
    client.patch(`/chat/threads/${productId}/tz`, fields),
  checkAccess: (productId: string | number) =>
    client.get(`/chat/access/${productId}`) as Promise<{ success: boolean; access: boolean; can_edit_tz: boolean }>,
  translate: (text: string, from: ChatLang, to: ChatLang) =>
    client.post('/translate', { text, from, to }) as Promise<{ success: boolean; text: string }>,
};

const LANG_KEY = 'product_chat_display_lang';

export function loadChatLang(): ChatLang {
  const v = localStorage.getItem(LANG_KEY);
  if (v === 'ru' || v === 'en' || v === 'zh') return v;
  return 'ru';
}

export function saveChatLang(lang: ChatLang) {
  localStorage.setItem(LANG_KEY, lang);
}

export function messageText(msg: ChatMessage, lang: ChatLang): string {
  const key = `body_${lang}` as keyof ChatMessage;
  return String(msg[key] || msg.body_original || '');
}

export function previewText(thread: ChatThread, lang: ChatLang): string {
  const map: Record<ChatLang, string | null | undefined> = {
    ru: thread.last_body_ru,
    en: thread.last_body_en,
    zh: thread.last_body_zh,
  };
  return String(map[lang] || thread.last_body_original || thread.title || thread.name || '');
}
